<template>
  <div class="ob-root">

    <!-- header rail. matches the erm rail treatment (steelblue accent,
         eyebrow + title) with the yard filter + refresh on the right.
         per-list counts live on the tiles below, so the rail stays lean. -->
    <div class="ob-rail">
      <div class="ob-rail-text">
        <div class="ob-rail-eyebrow">Ops &middot; Today</div>
        <h3 class="ob-rail-title">
          Today
          <span v-if="contextTenantName" class="ob-rail-tenant-chip">{{ contextTenantName }}</span>
        </h3>
        <div v-if="summary" class="ob-rail-sub">
          {{ boardDateLabel }}<span v-if="summary.timezone"> &middot; {{ summary.timezone }}</span>
        </div>
      </div>

      <div class="ob-rail-actions">
        <b-form-select
          v-model="selected_yard"
          :options="yardOptions"
          size="sm"
          class="ob-yard-select"
          :disabled="!summary"
          aria-label="Filter by base"
          @change="onYardChange"
        />
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="refreshing"
          title="Refresh the board"
          aria-label="Refresh"
          @click="manualRefresh"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': refreshing }"></i>
        </b-button>
      </div>
    </div>

    <!-- load failures surface inline so the board never goes blank;
         any data already on screen stays visible underneath. -->
    <div v-if="error" class="ob-error">
      <span>{{ error }}</span>
      <b-button size="sm" variant="outline-secondary" @click="manualRefresh">Retry</b-button>
    </div>

    <!-- first-load spinner only. background refreshes are silent. -->
    <div v-if="loading" class="ob-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading the board...
    </div>

    <!-- fleet assistant brief. mounted outside the board wrapper so its
         fetch runs in parallel with the board fetch; renders nothing when
         the tenant has the brief (or fleet assistant) turned off. -->
    <fa-brief-tile v-if="tenant" :tenant="tenant" :board-signature="board_signature" />

    <div v-if="!loading && board">

      <!-- top row: the two glance tiles. needs-attention (what the system
           flagged) sits next to the huddle (what the team logged this
           week), so the morning read is one screenful. -->
      <div class="ob-top-grid">

        <!-- needs attention tile. critical verdicts first, acknowledged
             ones collapsed into a muted expandable row at the bottom. -->
        <div class="ob-tile ob-attention">
          <div class="ob-tile-head">
            <div class="ob-tile-title">Needs attention</div>
            <span v-if="activeVerdicts.length" class="ob-lane-count ob-lane-count--attn">{{ activeVerdicts.length }}</span>
          </div>

          <div v-if="!activeVerdicts.length" class="ob-attention-empty">
            All clear. Nothing needs attention right now.
          </div>

          <!-- the verdict stack caps at ~46vh on rough mornings so the
               huddle and lanes stay on screen; same sticky overflow hint
               the lanes use. -->
          <div
            v-else
            ref="attention_list"
            class="ob-attention-list"
            @scroll.passive="updateAttentionMore"
          >
            <div
              v-for="v in activeVerdicts"
              :key="v.key"
              class="ob-verdict"
              :class="'ob-verdict--' + v.severity"
            >
              <div class="ob-verdict-main">
                <div class="ob-verdict-headline"><i class="fa ob-verdict-icon" :class="v.severity === 'critical' ? 'fa-exclamation-circle ob-verdict-icon--critical' : 'fa-exclamation-triangle ob-verdict-icon--warning'" aria-hidden="true"></i><span class="sr-only">{{ v.severity }}</span>{{ v.headline }}</div>
                <div class="ob-verdict-detail">{{ v.detail }}</div>
                <span v-if="v.matched_on" class="ob-chip ob-chip--matched">Matched on {{ v.matched_on }}</span>
              </div>
              <div class="ob-verdict-actions">
                <b-button
                  v-if="verdictLink(v)"
                  size="sm"
                  variant="outline-secondary"
                  :to="verdictLink(v).to"
                >{{ verdictLink(v).label }}</b-button>
                <b-button
                  v-if="canAct"
                  size="sm"
                  variant="outline-secondary"
                  :disabled="ack_pending.includes(v.key)"
                  @click="setAck(v, true)"
                >Acknowledge</b-button>
              </div>
            </div>
            <div v-if="laneMore('attention')" class="ob-lane-more">{{ laneMore('attention') }} more below</div>
          </div>

          <div v-if="acknowledgedVerdicts.length" class="ob-acked">
            <button type="button" class="ob-acked-toggle" @click="show_acked = !show_acked">
              <i class="fa" :class="show_acked ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              {{ acknowledgedVerdicts.length }} acknowledged
            </button>
            <div v-if="show_acked" class="ob-acked-list">
              <div v-for="v in acknowledgedVerdicts" :key="v.key" class="ob-acked-item">
                <span class="ob-acked-headline">{{ v.headline }}</span>
                <span v-if="v.acknowledged && v.acknowledged.by" class="ob-acked-by">by {{ shortUser(v.acknowledged.by) }}</span>
                <b-button
                  v-if="canAct"
                  size="sm"
                  variant="link"
                  class="ob-acked-undo"
                  :disabled="ack_pending.includes(v.key)"
                  @click="setAck(v, false)"
                >Undo</b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- weekly huddle recap, moved here from the erm list view so the
             team-facing digest reads alongside the day's exceptions. -->
        <huddle-panel :tenant="tenant" />
      </div>

      <!-- the three trip lanes, side by side so departures, vehicles out,
           and returns are all on screen at once (whiteboard-style). cards
           carry the same fields the old full-width tables did, including
           the click-to-edit vehicle status. -->
      <div class="ob-lanes">
        <div v-for="section in sections" :key="section.id" class="ob-tile ob-lane">
          <div class="ob-tile-head">
            <div class="ob-tile-title">{{ section.title }}</div>
            <span class="ob-lane-count">{{ section.items.length }}</span>
          </div>

          <div
            class="ob-lane-scroll"
            tabindex="0"
            role="region"
            :aria-label="section.title"
            :ref="'lane_' + section.id"
            @scroll.passive="updateLaneMore(section.id)"
          >
            <div v-for="item in section.items" :key="item.key" class="ob-card">
              <div class="ob-card-head">
                <span class="ob-dt" :class="{ 'ob-dt--overdue': isOverdue(section, item) }" :title="item[section.timeField]">
                  {{ section.clock ? formatTimeCompact(item[section.timeField]) : formatDateTimeCompact(item[section.timeField]) }}
                </span>
                <span class="ob-charter">{{ item.charter_id }}</span>
                <span v-if="stateChip(item)" class="ob-state-chip" :class="stateChip(item).cls">{{ stateChip(item).label }}</span>
              </div>
              <div class="ob-card-route" :title="routeText(item)">{{ routeText(item) }}</div>
              <div class="ob-card-meta">
                <span class="ob-vehicle-cell">
                  <b-form-select
                    v-if="canAct && item.vehicle && editing_key === item.key"
                    ref="status_select"
                    v-model="edit_status"
                    :options="vehicleStatusOptions"
                    size="sm"
                    class="ob-status-select"
                    :disabled="saving_status"
                    @change="saveVehicleStatus(item)"
                    @keyup.native.esc="cancelVehicleEdit"
                    @blur.native="onVehicleEditBlur"
                  />
                  <span
                    v-else-if="item.vehicle"
                    class="ob-vehicle-name"
                    :class="{ 'ob-vehicle-name--editable': canAct }"
                    :role="canAct ? 'button' : null"
                    :tabindex="canAct ? 0 : null"
                    :title="canAct ? 'Update vehicle status' : item.vehicle"
                    @click="startVehicleEdit(item)"
                    @keydown.enter.prevent="startVehicleEdit(item)"
                    @keydown.space.prevent="startVehicleEdit(item)"
                  >
                    {{ item.vehicle }}
                    <i v-if="canAct" class="fa fa-pencil ob-edit-glyph"></i>
                  </span>
                  <span v-else class="ob-chip ob-chip--unassigned">Unassigned</span>
                  <span v-if="readinessChip(item)" class="ob-chip" :class="readinessChip(item).cls">{{ readinessChip(item).label }}</span>
                </span>
                <span class="ob-card-sep">&middot;</span>
                <span v-if="item.driver">{{ item.driver }}</span>
                <span v-else class="ob-chip ob-chip--unassigned">Unassigned</span>
                <span
                  v-if="item.open_events && item.open_events.length"
                  class="ob-chip ob-chip--events"
                  role="button"
                  tabindex="0"
                  :title="eventsTooltip(item)"
                  :aria-label="item.open_events.length + ' open events, opens Event Response Manager'"
                  @click="goToOpsDesk(item)"
                  @keydown.enter.prevent="goToOpsDesk(item)"
                  @keydown.space.prevent="goToOpsDesk(item)"
                >{{ item.open_events.length }} open</span>
              </div>
            </div>
            <div v-if="!section.items.length" class="ob-empty-inline">{{ section.empty }}</div>
            <div v-if="laneMore(section.id)" class="ob-lane-more">{{ laneMore(section.id) }} more below</div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment/moment'
import { formatDateTimeCompact, formatTimeCompact } from '@/helpers/dates'
import { statusOptions } from '@/utils/vehicle_status_labels'
import { vsmRouteName } from '../utils/vsm_access'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import { get_ops_desk_board, ack_ops_desk_board, update_vehicle_status } from '../networking/apis'
import huddle_panel from './huddle_panel.vue'
import fa_brief_tile from './fa_brief_tile.vue'

// maintenance readiness chips. READY (and null) render no chip; the rest
// flag the vehicle with a tinted pill next to its name.
const READINESS_CHIPS = {
  PENDING_DVIR: { label: 'Pending DVIR', cls: 'ob-chip--amber' },
  UNSAFE_DVIR: { label: 'Unsafe DVIR', cls: 'ob-chip--red' },
  PM: { label: 'PM', cls: 'ob-chip--gray' },
  OOS: { label: 'OOS', cls: 'ob-chip--red' },
}

export default {
  name: 'ops_board',
  components: { 'huddle-panel': huddle_panel, 'fa-brief-tile': fa_brief_tile },
  data() {
    return {
      loading: false,
      refreshing: false,
      error: '',
      board: null,
      selected_yard: '',
      now_ms: Date.now(),
      fetch_seq: 0,
      refresh_timer: null,

      // reduced-projection hash of the latest board payload; the brief
      // tile watches it so the 60s tick only refetches the brief when
      // something material changed.
      board_signature: '',

      // acknowledgement state
      show_acked: false,
      ack_pending: [],

      // per-container count of items scrolled out of view below the fold
      // (the three lanes plus the attention stack)
      lane_more: {},

      // inline vehicle-status edit state. the stale-edit timer cancels an
      // abandoned editor so it cannot pause the auto-refresh indefinitely.
      editing_key: null,
      edit_status: null,
      saving_status: false,
      edit_timer: null,

      // debounce handle for the window-resize overflow recount
      resize_timer: null,

      // same settable statuses the vsm select offers (vehicle_status.vue
      // status_options minus its '' = ALL filter entry), with the shared
      // humanized labels.
      vehicleStatusOptions: [
        { value: null, text: 'Set status...', disabled: true },
        ...statusOptions(['READY', 'CLEANED', 'WASHED', 'DIRTY', 'OUT', 'OOS']),
      ],
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    summary() {
      return (this.board && this.board.summary) || null
    },
    boardDateLabel() {
      if (!this.summary || !this.summary.date) return ''
      const m = moment(this.summary.date, 'YYYY-MM-DD')
      return m.isValid() ? m.format('ddd, MMM D') : this.summary.date
    },
    yardOptions() {
      // the server list already leads with an all-bases entry; before it
      // arrives, the same text as a placeholder keeps the disabled control
      // legible instead of flashing an empty box
      if (!this.summary) return [{ value: '', text: 'All bases' }]
      return this.summary.base_options || []
    },
    // admin viewing a tenant other than their own: name the tenant in the
    // rail so the board's context is unambiguous.
    contextTenantName() {
      if (!tenantContextDiffers(this.$route, this.user)) return ''
      return (this.summary && this.summary.tenant_name) || tenantDisplayName(this.user, this.tenant)
    },
    // viewing is open to everyone with access; mutations (acknowledge,
    // inline vehicle status) are gated to operational roles. customer-admin
    // is the tenant-side fleet admin role (see role_display.js).
    canAct() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin' || role === 'manager' || role === 'dispatch'
    },
    activeVerdicts() {
      const verdicts = (this.board && this.board.attention) || []
      return verdicts
        .filter(v => !v.acknowledged)
        .sort((a, b) => this.severityRank(a) - this.severityRank(b))
    },
    acknowledgedVerdicts() {
      const verdicts = (this.board && this.board.attention) || []
      return verdicts.filter(v => !!v.acknowledged)
    },
    sections() {
      const b = this.board || {}
      return [
        { id: 'departing', title: 'Departing today', items: b.departing || [], timeField: 'start_time', clock: true, empty: 'No departures scheduled today.' },
        { id: 'out_now', title: 'Out now', items: b.out_now || [], timeField: 'start_time', empty: 'No vehicles are out right now.' },
        { id: 'returning', title: 'Returning today', items: b.returning || [], timeField: 'finish_time', clock: true, empty: 'No returns scheduled today.' },
      ]
    },
    vehicleStatusValues() {
      return this.vehicleStatusOptions.map(o => o.value).filter(v => !!v)
    },
    // mirrors vsm_shell.vue: resolve the tenant's vsm flavor from its
    // type + feature flags; falls back to the shell route when none apply.
    vsmRoute() {
      const u = this.user || {}
      const types = u.types || {}
      const features = u.features || {}
      return { name: vsmRouteName(types[this.tenant], features[this.tenant]), query: { tenant: this.tenant } }
    },
  },

  watch: {
    // the navbar tenant switcher rewrites ?tenant= without remounting the
    // component, so refetch (and drop the now-foreign yard filter).
    tenant() {
      this.selected_yard = ''
      this.board = null
      this.error = ''
      this.cancelVehicleEdit()
      this.load_data({ initial: true })
    },
  },

  mounted() {
    this.load_data({ initial: true })
    this.$nextTick(this.updateAllMore)

    // quiet auto-refresh. skips while a request is in flight or while the
    // inline status editor is open (a refetch would close it).
    this.refresh_timer = setInterval(() => {
      if (this.refreshing || this.editing_key) return
      this.load_data()
    }, 60000)

    // overflow counts depend on container heights (60vh / 46vh), so a
    // resize or rotation silently invalidates them
    window.addEventListener('resize', this.onWindowResize)
  },

  beforeDestroy() {
    if (this.refresh_timer) clearInterval(this.refresh_timer)
    this.clearEditTimer()
    window.removeEventListener('resize', this.onWindowResize)
    if (this.resize_timer) clearTimeout(this.resize_timer)
  },

  methods: {
    async load_data({ initial = false } = {}) {
      if (!this.tenant) return
      // sequence guard: overlapping fetches (yard change mid-refresh,
      // post-mutation refetch) only apply the latest response.
      const seq = ++this.fetch_seq
      this.refreshing = true
      if (initial || !this.board) this.loading = true
      try {
        const { value: { data = {} } = {} } = await get_ops_desk_board(this.tenant, this.selected_yard || undefined) || {}
        if (seq !== this.fetch_seq) return
        if (data && data.summary) {
          this.board = data
          this.now_ms = Date.now()
          this.board_signature = this.buildBoardSignature(data)
          this.error = ''
          this.$nextTick(this.updateAllMore)
        } else {
          this.error = 'The board came back empty. Retry in a moment.'
        }
      } catch (e) {
        if (seq === this.fetch_seq) {
          this.error = 'The board did not load. Check your connection and retry.'
        }
      } finally {
        if (seq === this.fetch_seq) {
          this.loading = false
          this.refreshing = false
        }
      }
    },

    manualRefresh() {
      this.load_data()
    },

    // mirrors the server-side brief fingerprint fields (and like it,
    // excludes the time-derived per-row state) so the brief refetch only
    // fires on material change. the server fingerprint stays authoritative;
    // this is just a refetch gate.
    buildBoardSignature(board) {
      if (!board || !board.summary) return ''
      const lanes = ['departing', 'out_now', 'returning'].map(lane =>
        (board[lane] || []).map(r => [
          r.key, r.vehicle || '', r.driver || '', r.vehicle_status || '',
          r.maintenance_status || '', r.hos_status || '',
          (r.open_events || []).map(e => e.id).sort().join(','),
        ].join('|')).sort().join(';')
      ).join('#')
      const verdicts = (board.attention || []).map(v =>
        [v.key, v.severity, (v.acknowledged && v.acknowledged.by) || ''].join('|')
      ).sort().join(';')
      const text = board.summary.date + '#' + lanes + '#' + verdicts
      let hash = 5381
      for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0
      }
      return String(hash)
    },

    onYardChange() {
      this.cancelVehicleEdit()
      this.load_data()
    },

    severityRank(v) {
      return v && v.severity === 'critical' ? 0 : 1
    },

    verdictLink(v) {
      const link = v && v.link
      if (!link) return null
      if (link.type === 'ops_event' && link.event_id) {
        // erm detail mode is the same route with an event_id query
        // (matches the distribution-email deep link shape).
        return { label: 'Open event', to: { name: 'ops_desk', query: { tenant: this.tenant, event_id: link.event_id } } }
      }
      if (link.type === 'vsm') {
        return { label: 'Open VSM', to: this.vsmRoute }
      }
      // booking links are omitted in v1
      return null
    },

    async setAck(verdict, acknowledged) {
      if (!this.canAct || !verdict || !this.summary) return
      if (this.ack_pending.includes(verdict.key)) return
      this.ack_pending.push(verdict.key)
      try {
        await ack_ops_desk_board(this.tenant, {
          date: this.summary.date,
          verdict_key: verdict.key,
          acknowledged: acknowledged,
        })
        await this.load_data()
      } catch (e) {
        this.toast('The acknowledgement did not save. Try again.')
      } finally {
        this.ack_pending = this.ack_pending.filter(k => k !== verdict.key)
      }
    },

    startVehicleEdit(item) {
      if (!this.canAct || !item || !item.vehicle) return
      this.clearEditTimer()
      this.editing_key = item.key
      const current = item.vehicle_status
      this.edit_status = this.vehicleStatusValues.includes(current) ? current : null
      // focus the select so the esc/blur cancel paths are live immediately;
      // without it the editor opens unfocused and only the timeout below
      // can close an abandoned edit
      this.$nextTick(() => {
        const ref = this.$refs.status_select
        const cmp = Array.isArray(ref) ? ref[0] : ref
        if (cmp && cmp.$el && cmp.$el.focus) cmp.$el.focus()
      })
      // stale-edit guard: a stray click cannot hold the editor (and the
      // paused auto-refresh) open indefinitely
      this.edit_timer = setTimeout(() => this.cancelVehicleEdit(), 90000)
    },

    cancelVehicleEdit() {
      this.clearEditTimer()
      this.editing_key = null
      this.edit_status = null
    },

    clearEditTimer() {
      if (this.edit_timer) {
        clearTimeout(this.edit_timer)
        this.edit_timer = null
      }
    },

    onVehicleEditBlur() {
      // @change fires before blur on selects, so skip the cancel while a
      // just-picked status is saving
      if (this.saving_status) return
      this.cancelVehicleEdit()
    },

    async saveVehicleStatus(item) {
      const status = this.edit_status
      const vehicle = item && item.vehicle
      this.cancelVehicleEdit()
      if (!status || !vehicle || this.saving_status) return
      this.saving_status = true
      try {
        // same payload shape vehicle_status.vue sends to the vsm endpoint:
        // vehicle name plus the changed field.
        await update_vehicle_status(this.tenant, { name: vehicle, vehicle_status: status })
        await this.load_data()
      } catch (e) {
        this.toast('The status update did not save. Try again.')
      } finally {
        this.saving_status = false
      }
    },

    readinessChip(item) {
      if (!item || !item.vehicle) return null
      const s = item.maintenance_status
      if (!s || s === 'READY') return null
      return READINESS_CHIPS[s] || { label: this.readableLabel(s), cls: 'ob-chip--gray' }
    },

    // truthful state chips: the chip claims only what the system can back.
    // the schedule phase is the default wording, a vsm OUT status counts as
    // positive evidence the bus is on the road, and an overdue_return
    // verdict is the red override, so the chip can never contradict the
    // needs-attention tile.
    stateChip(item) {
      const overdue = (item.attention_keys || []).some(k => String(k).indexOf('overdue_return:') === 0)
      if (item.state === 'returned') {
        if (overdue) return { label: 'Overdue', cls: 'ob-state-chip--overdue' }
        if (item.vehicle && item.vehicle_status === 'OUT') return { label: 'Due back', cls: 'ob-state-chip--due' }
        if (item.vehicle) return { label: 'Returned', cls: 'ob-state-chip--returned' }
        // unassigned slot, nothing truthful to claim
        return null
      }
      if (item.state === 'out') {
        if (item.vehicle && item.vehicle_status === 'OUT') return { label: 'Out', cls: 'ob-state-chip--out' }
        // calm gray: vsm hygiene is imperfect, so absence of OUT is not
        // evidence the bus is still in the yard
        if (item.vehicle) return { label: 'Due out', cls: 'ob-state-chip--scheduled' }
        return null
      }
      if (item.state === 'scheduled') return { label: 'Scheduled', cls: 'ob-state-chip--scheduled' }
      return { label: this.readableLabel(item.state), cls: 'ob-state-chip--scheduled' }
    },

    routeText(item) {
      const pickup = (item && item.pickup) || '-'
      const destination = (item && item.destination) || '-'
      return pickup + ' → ' + destination
    },

    isOverdue(section, item) {
      if (!section || section.id !== 'returning' || !item || !item.finish_time) return false
      if (item.state === 'returned') return false
      const t = new Date(item.finish_time).getTime()
      return Number.isFinite(t) && t < this.now_ms
    },

    eventsTooltip(item) {
      const evts = (item && item.open_events) || []
      return evts
        .map(e => this.readableLabel(e.entry_type) + ': ' + this.readableLabel(e.category) + ' (' + this.readableLabel(e.status) + ')')
        .join('\n')
    },

    goToOpsDesk(item) {
      // a card's open events all hang off its booking, so the chip lands
      // erm pre-filtered on that ref; plain navigation when there is none
      const query = { tenant: this.tenant }
      const ref = item && item.charter_id
      if (ref) query.search = String(ref)
      this.$router.push({ name: 'ops_desk', query }).catch(() => {})
    },

    laneMore(key) {
      return this.lane_more[key] || 0
    },

    // counts the items below the visible window of a scroll container so
    // the sticky "n more below" hint stays accurate (lanes + attention).
    updateMoreCount(refName, storeKey, selector) {
      const ref = this.$refs[refName]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (!el) return
      const hidden = Array.prototype.filter.call(
        el.querySelectorAll(selector),
        c => c.offsetTop >= el.scrollTop + el.clientHeight - 4
      ).length
      this.$set(this.lane_more, storeKey, hidden)
    },

    updateLaneMore(key) {
      this.updateMoreCount('lane_' + key, key, '.ob-card')
    },

    updateAttentionMore() {
      this.updateMoreCount('attention_list', 'attention', '.ob-verdict')
    },

    updateAllMore() {
      ['departing', 'out_now', 'returning'].forEach(k => this.updateLaneMore(k))
      this.updateAttentionMore()
    },

    onWindowResize() {
      if (this.resize_timer) clearTimeout(this.resize_timer)
      this.resize_timer = setTimeout(this.updateAllMore, 150)
    },

    readableLabel(v) {
      if (!v) return '-'
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    shortUser(username) {
      if (!username) return ''
      const at = String(username).indexOf('@')
      return at > 0 ? String(username).slice(0, at) : String(username)
    },

    toast(message) {
      try {
        this.$bvToast.toast(message, { title: 'Ops Board', variant: 'warning', solid: true, autoHideDelay: 4000 })
      } catch (_) { /* toast plugin may not be ready on first paint */ }
    },

    // compact cell timestamps, shared with the support and erm lists.
    formatDateTimeCompact,
    formatTimeCompact,
  },
}
</script>

<style scoped>
.ob-root {
  max-width: 1560px;
  margin: 0 auto;
  padding: 12px 32px 32px 32px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
@media (max-width: 720px) {
  .ob-root { padding: 12px 16px 24px 16px; }
}

/* header rail, matching the erm rail language */
.ob-rail {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side (support-ticket rail treatment) */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}
.ob-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  min-width: 0;
}
.ob-rail-eyebrow {
  font-size: 11px; line-height: 1.4;
  text-transform: uppercase; letter-spacing: 0.6px;
  color: var(--fi-ops-eyebrow); font-weight: 600;
  margin-bottom: 1px;
}
.ob-rail-title {
  margin: 0; padding: 0;
  font-size: 19px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
/* admin tenant-context chip: names whose data the page renders when the
   route tenant differs from the navbar selection. same treatment as the
   erm rail (.od-rail-tenant-chip). */
.ob-rail-tenant-chip {
  display: inline-flex; align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #c8d4df;
  color: var(--fi-ops-eyebrow);
  font-size: 11px; font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;
}
.ob-rail-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 1px;
  white-space: nowrap;
}
.ob-rail-actions {
  display: inline-flex; align-items: center; gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}
.ob-yard-select { width: 170px; }

@media (max-width: 600px) {
  .ob-rail { padding: 10px 12px; gap: 10px; }
  .ob-rail-title { font-size: 17px; }
  .ob-rail-actions { margin-left: 0; }
  .ob-yard-select { width: 140px; }
}

/* inline load error. muted, never replaces content already on screen. */
.ob-error {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; flex-wrap: wrap;
  background: #fff8ef;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #7a5400;
}

.ob-loading {
  padding: 36px 0;
  text-align: center;
  color: #6b7280;
  font-size: 13.5px;
}

/* tile system: every board region (attention, huddle, lanes) shares the
   same quiet card chrome so the grid reads as one whiteboard. */
.ob-tile {
  background: #fbfcfe;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
}
.ob-tile-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 8px;
}
.ob-tile-title {
  font-size: 12px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.ob-lane-count {
  display: inline-flex; align-items: center;
  padding: 0 7px; border-radius: 999px;
  font-size: 11.5px; font-weight: 700;
  background: #fff; border: 1px solid #c8d4df; color: var(--fi-ops-eyebrow);
  line-height: 1.6;
}
.ob-lane-count--attn {
  background: #fff4d1; border-color: #f0d97a; color: #7a5400;
}

/* top row: attention + huddle side by side; stacks under 1000px. */
.ob-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  margin-bottom: 14px;
}
@media (max-width: 1000px) {
  .ob-top-grid { grid-template-columns: minmax(0, 1fr); }
}

/* needs attention tile */
.ob-attention-list {
  max-height: 46vh;
  overflow-y: auto;
  padding-right: 2px;
  /* positioned so verdict offsetTop measures against this container; the
     more-below counter depends on it */
  position: relative;
}
.ob-attention-empty {
  font-size: 13.5px;
  color: #166534;
  background: #f2faf5;
  border: 1px solid #d4ecdc;
  border-radius: 6px;
  padding: 10px 14px;
}
.ob-verdict {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
}
/* the left border mirrors the severity icon color */
.ob-verdict--critical { border-left-color: #dc2626; }
.ob-verdict--warning  { border-left-color: #7a5400; }
.ob-verdict-main { min-width: 0; flex: 1 1 280px; }
.ob-verdict-headline { font-weight: 600; font-size: 13.5px; color: #1d1d1f; }
.ob-verdict-icon { margin-right: 6px; font-size: 12px; }
.ob-verdict-icon--critical { color: #dc2626; }
.ob-verdict-icon--warning { color: #7a5400; }
.ob-verdict-detail { font-size: 13px; color: #4b5563; margin-top: 2px; }
.ob-verdict-main .ob-chip--matched { margin-top: 6px; }
.ob-verdict-actions {
  display: inline-flex; gap: 6px; flex-shrink: 0; align-items: center;
}

/* acknowledged collapse row */
.ob-acked { margin-top: 4px; }
.ob-acked-toggle {
  background: none; border: none; padding: 2px 0;
  font-size: 12.5px; color: #6b7280; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
}
.ob-acked-toggle:hover { color: #4b5563; }
.ob-acked-toggle .fa { font-size: 10px; }
.ob-acked-list { margin-top: 4px; }
.ob-acked-item {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 12.5px; color: #6b7280;
  padding: 3px 0 3px 16px;
}
.ob-acked-headline { color: #6b7280; }
.ob-acked-by { font-size: 12px; }
.ob-acked-undo { padding: 4px 8px; margin: -4px 0; font-size: 12.5px; }

/* trip lanes: three across on a full screen, two on laptops, one on
   phones. each lane scrolls internally so the page height stays fixed
   for wall displays. */
.ob-lanes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  align-items: start;
}
@media (max-width: 1180px) {
  .ob-lanes { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 760px) {
  .ob-lanes { grid-template-columns: minmax(0, 1fr); }
}
.ob-lane-scroll {
  display: flex; flex-direction: column; gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 2px;
  /* positioned so card offsetTop measures against this container, not the
     page; the more-below counter depends on it */
  position: relative;
}
.ob-lane-scroll:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }

/* sticky overflow hint at the bottom of a scrollable lane */
.ob-lane-more {
  position: sticky;
  bottom: 0;
  margin-top: -18px;
  padding: 18px 0 2px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
  background: linear-gradient(180deg, rgba(251,252,254,0) 0%, #fbfcfe 60%);
  pointer-events: none;
}

.ob-empty-inline { color: #6b7280; font-style: italic; font-size: 13px; padding: 4px 0; }

/* trip cards (all widths; the former mobile-card pattern, promoted).
   no hover tint: the card itself is inert, only the vehicle name and the
   events chip act. */
.ob-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
}
.ob-card-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.ob-card-head .ob-state-chip { margin-left: auto; }
.ob-card-route {
  font-size: 13px; color: #374151; margin-top: 3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ob-card-meta {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 12.5px; color: #4b5563; margin-top: 5px;
}
.ob-card-sep { color: #c4cad2; }

/* cells */
.ob-dt {
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: #374151;
}
.ob-dt--overdue { color: #b91c1c; }
.ob-charter {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: #374151;
  white-space: nowrap;
}

.ob-vehicle-cell {
  display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.ob-vehicle-name { white-space: nowrap; }
.ob-vehicle-name--editable { cursor: pointer; }
.ob-vehicle-name--editable:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
/* always-visible pencil: the quiet signal that the name is editable */
.ob-edit-glyph {
  font-size: 10px; color: #6b7280;
}
.ob-status-select { width: 130px; }

/* small pills */
.ob-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  font-size: 11px;
  line-height: 1.5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 999px;
  border: 1px solid;
  white-space: nowrap;
}
.ob-chip--unassigned { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.ob-chip--red        { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.ob-chip--amber      { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.ob-chip--gray       { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
/* navigation affordance, not a status: gray at rest, livens on hover */
.ob-chip--events {
  background: #f1f3f5; color: #4b5563; border-color: #d6dde6;
  cursor: pointer; text-transform: none; letter-spacing: 0;
  font-size: 11.5px;
}
.ob-chip--events:hover { border-color: var(--fi-ops-accent); color: var(--fi-ops-accent-strong); }
.ob-chip--events:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.ob-chip--matched {
  background: #f1f3f5; color: #4b5563; border-color: #d6dde6;
  text-transform: none; letter-spacing: 0; font-weight: 500;
  font-size: 11.5px;
}

/* state chips */
.ob-state-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid;
  white-space: nowrap;
}
.ob-state-chip--scheduled { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.ob-state-chip--out       { background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb; }
.ob-state-chip--returned  { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }
.ob-state-chip--overdue   { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.ob-state-chip--due       { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
</style>
